"use client";

import React, { useRef, useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

interface FormTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  formContent: React.ReactNode;
  historyContent: React.ReactNode;
  /** When true, form flows with page scroll instead of using an inner scroll container */
  fullPage?: boolean;
  /** Optional counts to display as badges (e.g. number of history entries) */
  formCount?: number;
  historyCount?: number;
}

/**
 * Wraps the tab structure for form and history tabs
 */
export const FormTabs = React.memo(function FormTabs({
  activeTab,
  onTabChange,
  formContent,
  historyContent,
  fullPage = false,
  formCount,
  historyCount,
}: FormTabsProps) {
  // Single scroll: in modals let the modal body scroll; form tab has no inner scroll. On full page, same flow.
  const formTabClass = "space-y-4 mt-4";

  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
      {/* Tab bar */}
      <TabsList className="relative flex w-full rounded-xl bg-muted/60 p-1 shadow-inner border border-border/50 gap-1 h-auto">
        <TabsTrigger
          value="form"
          className={cn(
            "relative flex-1 flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200",
            "text-muted-foreground",
            // Active state: clean white card with shadow lift
            "data-[state=active]:bg-background data-[state=active]:text-foreground",
            "data-[state=active]:shadow-md data-[state=active]:shadow-black/8",
            "data-[state=active]:font-semibold",
            // Hover state for inactive
            "hover:text-foreground hover:bg-background/50",
            // Remove default ring/focus styles and replace
            "focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 outline-none",
          )}
        >
          {/* Icon */}
          <svg
            className="size-4 shrink-0 opacity-70"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M7 8h10M7 12h6M7 16h8" />
          </svg>
          <span>Form</span>
          {formCount !== undefined && (
            <Badge count={formCount} active={activeTab === "form"} />
          )}
        </TabsTrigger>

        <TabsTrigger
          value="history"
          className={cn(
            "relative flex-1 flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200",
            "text-muted-foreground",
            "data-[state=active]:bg-background data-[state=active]:text-foreground",
            "data-[state=active]:shadow-md data-[state=active]:shadow-black/8",
            "data-[state=active]:font-semibold",
            "hover:text-foreground hover:bg-background/50",
            "focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 outline-none",
          )}
        >
          {/* Icon */}
          <svg
            className="size-4 shrink-0 opacity-70"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 8v4l3 3" />
            <circle cx="12" cy="12" r="9" />
          </svg>
          <span>History</span>
          {historyCount !== undefined && (
            <Badge count={historyCount} active={activeTab === "history"} />
          )}
        </TabsTrigger>
      </TabsList>

      {/* Tab content with subtle fade-in */}
      <TabsContent
        value="form"
        className={cn(formTabClass, "focus-visible:outline-none")}
        style={{ animationDuration: "150ms" }}
      >
        {formContent}
      </TabsContent>

      <TabsContent
        value="history"
        className="space-y-4 focus-visible:outline-none"
        style={{ animationDuration: "150ms" }}
      >
        {historyContent}
      </TabsContent>
    </Tabs> 
  );
});

/** Small count badge for tabs */
function Badge({ count, active }: { count: number; active: boolean }) {
  if (count === 0) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-semibold tabular-nums transition-colors duration-200",
        active
          ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
          : "bg-muted-foreground/15 text-muted-foreground",
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}